import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import InfiniteAutocomplete, { InfiniteAutocompleteProps } from './InfiniteAutocomplete';
import {
    AxiosInfinitePageableResponse,
    PAGEABLE_SIZE,
    PageableRequest,
    PageableResponse,
} from '../types/AxiosPageableResponse';
import Utils from './Utils';
import { Oem } from '../types/Oem';
import { OemCriteria } from '../types/Criteria/OemCriteria';
import { useStateTransitions } from '../hooks/Transition';

type EmptyTransitionState = 'empty' | 'not-empty';

const getOptionKey = (option: Oem | any): string | number => {
    return option?.id as number;
};

const getOptionLabel = (option: Oem | any): string => {
    return option?.name || '';
};

const defaultPageable: PageableRequest = {
    page: 1,
    size: PAGEABLE_SIZE,
    sort: 'asc',
    sortBy: 'name',
};

const fetch = async (
    criteria: OemCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<PageableResponse<Oem>> => {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) source.cancel('aborted');
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    const response = await axios.get('/api/oems/filter', {
        headers: { XBusy: 'false' },
        params: {
            ...criteria,
            ...pageable,
            page: page - 1,
            ids: criteria?.ids?.join(',') || undefined,
            oemProgramIds: criteria?.oemProgramIds?.join(',') || undefined,
            agencyIds: criteria?.agencyIds?.join(',') || undefined,
            advertiserIds: criteria?.advertiserIds?.join(',') || undefined,
            agencyMarketRegions: criteria?.agencyMarketRegions?.join(',') || undefined,
        },
        cancelToken: source.token,
    });
    return response.data;
};

const fetchInitial = async (
    ids: number[] | number | null | undefined,
    criteria: OemCriteria,
    signal?: AbortSignal
): Promise<any> => {
    const _ids: number[] = Array.isArray(ids) ? ids : ids != null ? [ids] : [];

    if (_ids.length === 0) {
        return [];
    }

    const { content } = await fetch({ ...criteria, ids: _ids }, { page: 1, size: 1000 }, signal);

    return content;
};

const fetchInfinite = async (
    criteria: OemCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<AxiosInfinitePageableResponse<Oem>> => {
    const { content } = await fetch(criteria, { page, ...pageable }, signal);

    return {
        items: content,
        nextPage: content.length === pageable.size ? page + 1 : undefined,
    };
};

const fetchMass = async (criteria: OemCriteria, signal?: AbortSignal): Promise<any> => {
    const { content } = await fetch(criteria, { page: 1, size: 1000 }, signal);

    return content;
};

interface OemCheckboxAutoCompleteProps<
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> extends Omit<
        InfiniteAutocompleteProps<Oem, Multiple, DisableClearable, FreeSolo>,
        'options' | 'value' | 'getOptionKey' | 'getOptionLabel'
    > {
    onLoading?: () => void;
    onEmpty?: () => void;
    onNotEmpty?: () => void;
    criteria?: OemCriteria;
    value: Multiple extends true ? number[] : number | null;
    autoSelectSingleOption?: boolean;
    deferChangeUntilClose?: boolean;
}

const OemCheckboxAutoComplete = <
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    onLoading = () => {},
    onEmpty = () => {},
    onNotEmpty = () => {},
    onChange,
    disabled = false,
    autoSelectSingleOption = false,
    deferChangeUntilClose = false,
    ...props
}: OemCheckboxAutoCompleteProps<Multiple, DisableClearable, FreeSolo>) => {
    const { debounce } = Utils;

    const [value, setValue] = useState<InfiniteAutocompleteProps<Oem, Multiple, DisableClearable, FreeSolo>['value']>(
        (props.multiple ? [] : null) as InfiniteAutocompleteProps<Oem, Multiple, DisableClearable, FreeSolo>['value']
    );
    const [inputValue, setInputValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMassSelected, setIsMassSelected] = useState<boolean>(false);
    const pendingValueRef = useRef<
        InfiniteAutocompleteProps<Oem, Multiple, DisableClearable, FreeSolo>['value'] | null
    >(null);
    const hasChangedWhileOpenRef = useRef<boolean>(false);

    const criteriaWithoutName = useMemo((): OemCriteria => {
        return {
            ids: props.criteria?.ids,
            oemProgramIds: props?.criteria?.oemProgramIds || [],
            agencyIds: props?.criteria?.agencyIds || [],
            advertiserIds: props?.criteria?.advertiserIds || [],
            agencyMarketRegions: props?.criteria?.agencyMarketRegions || [],
        };
    }, [props.criteria]);

    const criteria = useMemo((): OemCriteria => {
        return {
            ...criteriaWithoutName,
            name: inputValue,
        };
    }, [criteriaWithoutName, inputValue]);

    // Fetch initial value if IDs are provided
    const { data: initialValue, isLoading: isLoadingInitialValue } = useQuery({
        queryKey: ['infinite/oems/initial', props.value, criteriaWithoutName],
        queryFn: ({ signal }) => fetchInitial(props.value, criteria, signal),
        enabled: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (initialValue) {
            if (props.multiple) {
                setValue((initialValue ?? []) as any);
            } else {
                setValue((initialValue[0] ?? null) as any);
            }
        }
    }, [initialValue, props.multiple]);

    const { data, fetchNextPage, hasNextPage, isFetched, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
        queryKey: ['infinite/oems', criteria],
        queryFn: ({ pageParam, signal }) =>
            fetchInfinite(criteria, { page: pageParam || 1, size: defaultPageable.size }, signal),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
        // staleTime: 1000 * 60 * 5,
        enabled: true,
        refetchOnWindowFocus: false,
    });

    const options = useMemo<Oem[]>(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

    const { refetch: refetchMass, isLoading: isLoadingMass } = useQuery({
        queryKey: ['infinite/oems/mass', criteria],
        queryFn: ({ signal }) => fetchMass(criteria, signal),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const handleOnClose = useCallback((): void => {
        setIsOpen(false);
        if (deferChangeUntilClose && hasChangedWhileOpenRef.current && pendingValueRef.current !== null) {
            onChange(pendingValueRef.current as any);
        }
        hasChangedWhileOpenRef.current = false;
        pendingValueRef.current = null;
    }, [deferChangeUntilClose, onChange]);

    const handleOnChange = useCallback(
        (
            newValue: InfiniteAutocompleteProps<Oem, Multiple, DisableClearable, FreeSolo>['value'] | undefined,
            reason?: AutocompleteChangeReason
        ): void => {
            setValue(newValue);
            if (deferChangeUntilClose) {
                pendingValueRef.current = newValue ?? null;
                hasChangedWhileOpenRef.current = true;
            } else {
                onChange(newValue as any);
            }

            // If cleared via X button, trigger close logic immediately
            if (reason === 'clear') {
                handleOnClose();
            }
        },
        [deferChangeUntilClose, handleOnClose, onChange]
    );

    const debouncedOnInputChange = useRef(
        debounce((newInputValue: string): void => {
            refetch();
        }, 1000)
    ).current;

    const isLoading = useMemo((): boolean => {
        return isFetching || isFetchingNextPage || isLoadingInitialValue || isLoadingMass;
    }, [isFetching, isFetchingNextPage, isLoadingInitialValue, isLoadingMass]);

    const isSearching = inputValue.trim().length > 0;
    const isDisabled = disabled || (!isLoading && options.length === 0 && !isSearching);

    useStateTransitions<EmptyTransitionState>({
        conditions: {
            empty: () => isFetched && !isLoading && !isSearching && data !== undefined && options.length === 0,
            'not-empty': () => isFetched && !isLoading && !isSearching && data !== undefined && options.length > 0,
        },
        callbacks: {
            empty: () => onEmpty?.(),
            'not-empty': () => onNotEmpty?.(),
        },
    });

    const handleOnInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason
    ): void => {
        setInputValue(newInputValue);
        debouncedOnInputChange(newInputValue);
    };

    const handleOnScroll = useCallback((): void => {
        if (!isMassSelected) {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isMassSelected]);

    useEffect((): void => {
        if (isLoading === true) {
            onLoading();
        }
    }, [isLoading, onLoading]);

    useEffect(() => {
        if (isOpen === false) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        setIsMassSelected(false);
    }, [criteria]);

    const handleOnFetchMass = async (): Promise<ReadonlyArray<Oem>> => {
        const { data } = await refetchMass();

        setIsMassSelected(true);

        return data;
    };

    const isAlreadySelected = useCallback(
        (only: Oem): boolean => {
            if (props.multiple) {
                const current = (value as unknown as Oem[] | null | undefined) ?? [];
                return Array.isArray(current) && current.some((v) => v?.id === only.id);
            }

            const current = value as unknown as Oem | null | undefined;
            return (current?.id ?? null) === only.id;
        },
        [props.multiple, value]
    );

    useEffect(() => {
        if (!autoSelectSingleOption) return;
        if (isLoading) return;
        if (isSearching) return;
        if (isMassSelected) return;

        if (options.length !== 1) return;

        const only = options[0];
        if (!only) return;

        if (isAlreadySelected(only)) return;

        const nextValue = (props.multiple ? [only] : only) as InfiniteAutocompleteProps<
            Oem,
            Multiple,
            DisableClearable,
            FreeSolo
        >['value'];

        handleOnChange(nextValue);
    }, [
        autoSelectSingleOption,
        isLoading,
        isSearching,
        isMassSelected,
        options,
        props.multiple,
        handleOnChange,
        isAlreadySelected,
    ]);

    return (
        <Tooltip
            arrow
            disableHoverListener={!isDisabled}
            title="The OEM filter is disabled because there are no OEMs matching the current filter selections."
        >
            <InfiniteAutocomplete
                disableCloseOnSelect={props.multiple}
                openOnFocus={props.multiple}
                selectOnFocus={props.multiple}
                blurOnSelect={false}
                clearOnBlur={false}
                label="OEM"
                {...props}
                disabled={isDisabled}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                options={options}
                value={value}
                loading={isLoading}
                onChange={handleOnChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                onScroll={handleOnScroll}
                onFetchMass={handleOnFetchMass}
                onOpen={(): void => setIsOpen(true)}
                onClose={handleOnClose}
            />
        </Tooltip>
    );
};

export type { OemCheckboxAutoCompleteProps };
export default OemCheckboxAutoComplete;
